import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import {
  DevtoolsGetBillingPreviewResponse,
  DevtoolsGetPatientResponse,
  EraPanelConfirmParams,
  EraPanelResponse,
  GetPreviewInputs,
  InvoicePanelParams,
  InvoicePanelResponse,
  SearchFormInputs,
  TransactionDebugParams,
  TransactionDebugResponse,
} from '@chiroup/core/types/Devtools.type';

const devtoolsService = () => {
  const transactionDebug = {
    get: async (body: TransactionDebugParams): Promise<TransactionDebugResponse> => {
      body.fn = 'get';
      const res = await ChiroUpAPI.post('api', `/devtools/transaction-debug`, {
        body,
      });

      return res;
    },
  };
  const invoicePanel = {
    get: async (body: InvoicePanelParams): Promise<InvoicePanelResponse> => {
      body.fn = 'get';

      if (typeof body.sInvoices === 'string') {
        body.invoices = body.sInvoices.split(/\s+/);
        delete body.sInvoices;
      }

      const res = await ChiroUpAPI.post('api', `/devtools/invoice-panel`, {
        body,
      });

      return res;
    },
    move: async (body: InvoicePanelParams): Promise<InvoicePanelResponse> => {
      body.fn = 'move';

      if (typeof body.sInvoices === 'string') {
        body.invoices = body.sInvoices.split(/\s+/);
        delete body.sInvoices;
      }

      const res = await ChiroUpAPI.post('api', `/devtools/invoice-panel`, {
        body,
      });

      return res;
    },
  };
  const eraPanel = {
    get: async (body: InvoicePanelParams): Promise<EraPanelResponse> => {
      body.fn = 'get';

      if (typeof body.sInvoices === 'string') {
        body.invoices = body.sInvoices.split(/\s+/);
        delete body.sInvoices;
      }

      const res = await ChiroUpAPI.post('api', `/devtools/era-panel`, {
        body,
      });

      return res;
    },
    preflightClaimResponses: async (body: EraPanelConfirmParams) => {
      // preflight uses purge stuff...
      body.fn = 'preflight-claim-responses';
      const res = await ChiroUpAPI.post('api', `/devtools/era-panel`, {
        body,
      });

      return res;
    },
    confirmed: async (body: EraPanelConfirmParams) => {
      body.fn = 'confirmed';
      const res = await ChiroUpAPI.post('api', `/devtools/era-panel`, {
        body,
      });

      return res;
    },
  };
  const patientPurge = {
    getPatient: async (body: SearchFormInputs): Promise<DevtoolsGetPatientResponse> => {
      const issues: string[] = [];
      console.log({ body });
      if (!body.clinicId) issues.push('clinicId is required');
      if (!body.fname) issues.push('fname is required');
      if (!body.lname) issues.push('lname is required');

      if (issues.length > 0) {
        throw new Error(issues.join(', '));
      }

      (body as any).fn = 'get';
      const res = await ChiroUpAPI.post('api', `/devtools/get-patient`, {
        body,
      });

      return res;
    },
    getPreview: async (body: GetPreviewInputs): Promise<DevtoolsGetBillingPreviewResponse> => {
      const issues: string[] = [];
      console.log({ body });
      if (!body.clinicId) issues.push('clinicId is required');
      if (!(body?.ids ?? []).length) issues.push('list of ids is required');

      if (issues.length > 0) {
        throw new Error(issues.join(', '));
      }

      (body as any).fn = 'preview';
      const res = await ChiroUpAPI.post('api', `/devtools/patient-purge`, {
        body,
      });

      return res;
    },
    purgePreview: async (body: any): Promise<any> => {
      const issues: string[] = [];
      console.log({ purgePreview: body });
      if (!body.clinicId) issues.push('clinicId is required');
      if (!(body?.list ?? []).length) issues.push('list of keys is required');

      if (issues.length > 0) {
        throw new Error(issues.join(', '));
      }

      (body as any).fn = 'purge-preview';
      const res = await ChiroUpAPI.post('api', `/devtools/patient-purge`, {
        body,
      });

      return res;
    },
    postPurgeActions: async (body: any): Promise<any> => {
      const issues: string[] = [];
      console.log({ postPurgeActions: body });

      if (!body || !Array.isArray(body?.actions)) {
        issues.push('An array of actions is required.');
      }

      if (issues.length > 0) {
        throw new Error(issues.join(', '));
      }

      (body as any).fn = 'purge-actions';
      const res = await ChiroUpAPI.post('api', `/devtools/patient-purge/${body?.clinicId}`, {
        body,
      });

      return res;
    },
  };
  const billingKeyPanel = {
    processSingle: async (body: {
      billingKey?: string;
      billingKeys?: string[];
      tableName?: string;
    }): Promise<any> => {
      const requestBody: any = {
        fn: 'processSingle',
      };

      if (body.billingKey) {
        requestBody.billingKey = body.billingKey;
      }

      if (body.billingKeys) {
        requestBody.billingKeys = body.billingKeys;
      }

      if (body.tableName) {
        requestBody.tableName = body.tableName;
      }

      const res = await ChiroUpAPI.post('api', `/devtools/billing-key-panel`, {
        body: requestBody,
      });

      return res;
    },
    getOutdatedInvoices: async (body: { clinicId: number; limit?: number }): Promise<any> => {
      const requestBody = {
        fn: 'getOutdatedInvoices',
        clinicId: body.clinicId,
        limit: body.limit,
      };

      const res = await ChiroUpAPI.post('api', `/devtools/billing-key-panel`, {
        body: requestBody,
      });

      return res;
    },
    getOutdatedInvoicesCount: async (body: { clinicId: number }): Promise<any> => {
      const requestBody = {
        fn: 'getOutdatedInvoicesCount',
        clinicId: body.clinicId,
      };

      const res = await ChiroUpAPI.post('api', `/devtools/billing-key-panel`, {
        body: requestBody,
      });

      return res;
    },
    getUnrebuiltFromTable: async (body: { tableName: string; limit?: number }): Promise<any> => {
      const requestBody = {
        fn: 'getUnrebuiltFromTable',
        tableName: body.tableName,
        limit: body.limit,
      };

      const res = await ChiroUpAPI.post('api', `/devtools/billing-key-panel`, {
        body: requestBody,
      });

      return res;
    },
    getUnrebuiltCountFromTable: async (body: { tableName: string }): Promise<any> => {
      const requestBody = {
        fn: 'getUnrebuiltCountFromTable',
        tableName: body.tableName,
      };

      const res = await ChiroUpAPI.post('api', `/devtools/billing-key-panel`, {
        body: requestBody,
      });

      return res;
    },
  };

  const automationPanel = {
    getIntegrations: async (body: { clinicId: number }): Promise<any> => {
      const requestBody = {
        fn: 'getIntegrations',
        clinicId: body.clinicId,
      };

      const res = await ChiroUpAPI.post('api', `/devtools/automation-panel`, {
        body: requestBody,
      });

      return res;
    },
    getIntegrationLogs: async (body: {
      integrationId: number;
      level?: string;
      startKey?: Record<string, any>;
      startDate?: number;
      endDate?: number;
    }): Promise<any> => {
      const requestBody: any = {
        fn: 'getIntegrationLogs',
        integrationId: body.integrationId,
      };

      if (body.level) {
        requestBody.level = body.level;
      }

      if (body.startKey) {
        requestBody.startKey = body.startKey;
      }

      if (body.startDate) {
        requestBody.startDate = body.startDate;
      }

      if (body.endDate) {
        requestBody.endDate = body.endDate;
      }

      const res = await ChiroUpAPI.post('api', `/devtools/automation-panel`, {
        body: requestBody,
      });

      return res;
    },
  };

  const patientPanel = {
    recalculateBalances: async (body: { patientIds: string[] }): Promise<any> => {
      const res = await ChiroUpAPI.post('api', `/devtools/patient-panel`, {
        body: {
          fn: 'recalculateBalances',
          ...body,
        },
      });

      return res;
    },
  };

  const billingPanel = {
    getBillingProfileUsage: async (body: { clinicId: number }): Promise<any> => {
      const requestBody = {
        fn: 'getBillingProfileUsage',
        clinicId: body.clinicId,
      };

      const res = await ChiroUpAPI.post('api', `/devtools/billing-panel`, {
        body: requestBody,
      });

      return res;
    },
    getActiveBillingProfiles: async (body: { clinicId: number }): Promise<any> => {
      const requestBody = {
        fn: 'getActiveBillingProfiles',
        clinicId: body.clinicId,
      };

      const res = await ChiroUpAPI.post('api', `/devtools/billing-panel`, {
        body: requestBody,
      });

      return res;
    },
    previewBillingProfileUpdate: async (body: {
      clinicId: number;
      onlyNulls: boolean;
      sourceBillingProfileId: number | null;
    }): Promise<any> => {
      const requestBody = {
        fn: 'previewBillingProfileUpdate',
        clinicId: body.clinicId,
        onlyNulls: body.onlyNulls,
        sourceBillingProfileId: body.sourceBillingProfileId,
      };

      const res = await ChiroUpAPI.post('api', `/devtools/billing-panel`, {
        body: requestBody,
      });

      return res;
    },
    updateBillingProfileIds: async (body: {
      clinicId: number;
      onlyNulls: boolean;
      sourceBillingProfileId: number | null;
      targetBillingProfileId: number | null;
    }): Promise<any> => {
      const requestBody = {
        fn: 'updateBillingProfileIds',
        clinicId: body.clinicId,
        onlyNulls: body.onlyNulls,
        sourceBillingProfileId: body.sourceBillingProfileId,
        targetBillingProfileId: body.targetBillingProfileId,
      };

      const res = await ChiroUpAPI.post('api', `/devtools/billing-panel`, {
        body: requestBody,
      });

      return res;
    },
  };

  return {
    automationPanel,
    billingKeyPanel,
    billingPanel,
    eraPanel,
    invoicePanel,
    patientPanel,
    transactionDebug,
    patientPurge,
  };
};

export default devtoolsService();
